/* Shared storefront enhancements. Native links and product forms remain usable without JS. */
if (!customElements.get('shipping-rates')) {
  customElements.define('shipping-rates', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      const link = this.querySelector('[data-rates-open]');
      const dialog = this.querySelector('dialog');
      const status = this.querySelector('[data-rates-status]');
      const output = this.querySelector('[data-rates-tables]');
      if (!link || !dialog?.showModal) return;
      link.addEventListener('click', async event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        dialog.showModal();
        if (this.loading) return;
        this.loading = true;
        status.textContent = status.dataset.loading;
        output.replaceChildren();
        try {
          const response = await fetch(link.href, {signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Shipping page unavailable');
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const tables = [...page.querySelectorAll('main table')];
          if (!tables.length) throw new Error('No shipping tables');
          for (const source of tables) {
            const wrapper = document.createElement('div');
            wrapper.className = 'le-rates-table-wrap';
            const table = document.createElement('table');
            // Copy text only, never scripts, handlers or remote page markup.
            for (const sourceRow of source.rows) {
              const row = table.insertRow();
              for (const sourceCell of [...sourceRow.cells].slice(0, 3)) {
                const cell = document.createElement(sourceCell.tagName === 'TH' ? 'th' : 'td');
                if (cell.tagName === 'TH') cell.scope = 'col';
                cell.textContent = sourceCell.textContent;
                row.append(cell);
              }
            }
            wrapper.append(table);
            output.append(wrapper);
          }
          status.textContent = '';
        } catch (error) {
          if (!signal.aborted) status.textContent = status.dataset.error;
        } finally {
          this.loading = false;
        }
      }, {signal});
      dialog.addEventListener('click', event => {
        if (event.target === dialog) {
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        }
      }, {signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}
if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const track = this.querySelector('.le-gallery__track');
      const items = [...this.querySelectorAll('.le-gallery__item')];
      if (!items.length || !track) return;
      this.querySelector('.le-gallery__controls').hidden = false;
      const update = () => {
        const index = Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        const count = this.querySelector('[data-gallery-count]');
        if (count) count.textContent = `${index + 1} / ${items.length}`;
        const prev = this.querySelector('[data-gallery-prev]');
        const next = this.querySelector('[data-gallery-next]');
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === items.length - 1;
        return index;
      };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-gallery-prev], [data-gallery-next]')) {
          const direction = event.target.closest('[data-gallery-next]') ? 1 : -1;
          track.scrollTo({left: (update() + direction) * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }
      }, {signal});
      track.addEventListener('scroll', update, {passive: true, signal});
      window.addEventListener('resize', update, {signal});
      update();
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('mobile-product-buy')) {
  customElements.define('mobile-product-buy', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      const section = this.closest('.shopify-section');
      const original = section?.querySelector('[data-add-to-cart]');
      const button = this.querySelector('button');
      if (!original || !button) return;
      const sync = () => {
        if (button.innerHTML !== original.innerHTML) button.innerHTML = original.innerHTML;
        button.className = original.className;
        button.disabled = original.disabled;
        this.hidden = original.getBoundingClientRect().bottom >= 0 || !!document.querySelector('dialog[open], details[scroll-lock][open], [data-cookie-banner]:not([hidden])') || matchMedia('(min-width: 990px)').matches;
      };
      // A fast scroll can jump from below to above the viewport without an
      // intersection event. Check position on scroll as well as state changes.
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; sync(); });
      }, {passive: true, signal});
      this.mutation = new MutationObserver(sync);
      this.mutation.observe(original, {childList: true, subtree: true, characterData: true, attributes: true});
      this.dialogMutation = new MutationObserver(sync);
      document.querySelectorAll('dialog, details[scroll-lock], [data-cookie-banner]').forEach(dialog => this.dialogMutation.observe(dialog, {attributes: true, attributeFilter: ['open', 'hidden']}));
      button.addEventListener('click', () => original.click(), {signal});
      window.addEventListener('resize', sync, {signal});
      sync();
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.mutation?.disconnect();
      this.dialogMutation?.disconnect();
    }
  });
}
